import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { BrandLogo } from "@/components/BrandLogo";
import { authClient, authErrorMessage } from "@/lib/auth-client";
import { useInitialFocus } from "@/hooks/useInitialFocus";

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const token = new URLSearchParams(window.location.search).get("token");
  const tokenError = new URLSearchParams(window.location.search).get("error");
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const passwordRef = useInitialFocus<HTMLInputElement>(Boolean(token) && !tokenError);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (password !== confirmation) {
      setError("Passwords do not match.");
      return;
    }
    if (!token) return;

    setLoading(true);
    setError(null);
    const { error: resetError } = await authClient.resetPassword({ newPassword: password, token });
    setLoading(false);
    if (resetError) {
      setError(authErrorMessage(resetError, "Could not reset your password."));
      return;
    }
    navigate({ to: "/login" });
  }

  const invalidToken = !token || tokenError;
  return (
    <div className="auth-shell">
      <div className="auth-card">
        <BrandLogo className="auth-logo" size={22} />
        <h1 className="auth-title">Choose a new password</h1>
        <p className="auth-subtitle">
          {invalidToken ? "This password reset link is invalid or has expired." : "Enter a new password for your Rivets account."}
        </p>

        {!invalidToken && (
          <form className="panel" onSubmit={handleSubmit}>
            <div className="field">
              <label className="label" htmlFor="new-password">
                New password
              </label>
              <input
                id="new-password"
                className="input"
                ref={passwordRef}
                type="password"
                autoComplete="new-password"
                required
                minLength={8}
                value={password}
              onChange={(event) => setPassword(event.target.value)}
              />
              <div className="hint">Use at least 8 characters.</div>
            </div>
            <div className="field">
              <label className="label" htmlFor="confirm-password">
                Confirm new password
              </label>
              <input
                id="confirm-password"
                className="input"
                type="password"
                autoComplete="new-password"
                required
                minLength={8}
                value={confirmation}
                onChange={(event) => setConfirmation(event.target.value)}
              />
            </div>
            <button className="btn btn-primary btn-block" type="submit" disabled={loading}>
              {loading ? "Updating…" : "Update password"}
            </button>
            {error && <div className="error-text">{error}</div>}
          </form>
        )}

        {invalidToken && <div className="panel auth-error-state" role="alert"><div className="auth-state-icon">!</div><strong>Link unavailable</strong><span>This reset link is invalid or expired.</span><Link className="btn btn-primary" to="/forgot-password">Request a new link</Link></div>}
      </div>
    </div>
  );
}
